import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { orderBy } from "lodash-es";
import { Fragment, useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

import type { CommunityFeedItemV2DtoPaginationResultDto } from "@/api/types";
import { AudiencePreview } from "@/components/AudienceSelector/AudiencePreview";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { IconButton } from "@/components/Button/IconButton";
import { CommunityMessage } from "@/components/CommunityFeed/CommunityMessage";
import { CommunityPoll } from "@/components/CommunityFeed/CommunityPoll";
import { CommunitySurvey } from "@/components/CommunityFeed/CommunitySurvey";
import { ResidentCreatePostModal } from "@/components/CommunityFeed/ResidentCreatePostModal";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { EntityDetailsCard } from "@/components/EntityDetailsCard/EntityDetailsCard";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Award01Icon } from "@/components/Icons/Award01Icon";
import { Building02Icon } from "@/components/Icons/Building02Icon";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { Copy01Icon } from "@/components/Icons/Copy01Icon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { MessageChatSquareIcon } from "@/components/Icons/MessageChatSquareIcon";
import { MessageQuestionSquareIcon } from "@/components/Icons/MessageQuestionSquareIcon";
import { PlusIcon } from "@/components/Icons/PlusIcon";
import { Power01Icon } from "@/components/Icons/Power01Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { AutoTranslatedLabel } from "@/components/Label/AutoTranslatedLabel";
import { LastActivityLabel } from "@/components/Label/LastActivityLabel";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { UserAvatarList } from "@/components/UserAvatarList/UserAvatarList";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { Card, CardCenteredState } from "@/design-system/Card";
import { CollectionLoadingState } from "@/design-system/CollectionState";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Stack } from "@/design-system/Stack";
import { ToggleButton } from "@/design-system/ToggleButton";
import type { MessageRouteStates } from "@/helpers/community-feed";
import { isContentAutoTranslated } from "@/helpers/languages";
import { canCreateInterestGroup } from "@/helpers/permissions";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { canClone } from "@/modules/community-groups/permissions";
import { CommunityPostViewCountTrackingProvider } from "@/providers/CommunityPostViewCountTrackingProvider";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { communityGroupMutations } from "@/queries/community-groups/mutations";
import { useCommunityGroupQueries } from "@/queries/community-groups/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { ApiQueryParams } from "@/types/api-types";

import { GroupMembersModal } from "../../components/GroupMembersModal";

const COMMUNITY_FEED_ITEM_AMOUNT = 20;

export function GroupDetailsPage(): React.ReactNode {
  const { id: groupId } = useParams<{
    id: string;
  }>();
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const slug = useSlug();
  const hasPermission = usePermission();
  const navigate = useNavigate();
  const [isGroupMembersModalOpened, groupMembersModalOpenHandlers] = useBool();
  const { componentProps: deleteModalProps, openDeleteModal } = useDeleteModal<string>("group-delete-modal");
  const [disableGroupId, setDisableGroupId] = useState<string | null>(null);
  const [residentPostType, setResidentPostType] = useState<"askHelp" | "inform" | "sellItem" | "promote" | null>(null);

  const communityGroupQueries = useCommunityGroupQueries();
  const {
    data: groupDetails,
    isLoading: groupDetailsIsFetching,
    error: groupDetailsError,
  } = useQuery({
    ...communityGroupQueries.details({
      groupId: groupId!,
    }),
    enabled: !!groupId,
  });
  const enableGroupMutation = communityGroupMutations.useEnableGroup();
  const disableGroupMutation = communityGroupMutations.useDisableGroup();
  const skipPromoteMutation = communityGroupMutations.useSkipGroupPromotion(groupId!);
  const followMutation = communityGroupMutations.useFollowGroup();
  const unfollowMutation = communityGroupMutations.useUnfollowGroup();
  const deleteGroupMutation = communityGroupMutations.useDeleteGroup();
  const communityFeedQueries = useCommunityFeedQueries();
  const messageFeedQuery = {
    Tab: "all",
    Connection: "included",
    GroupIds: [groupId!],
    FeedTypes: ["messages", "polls", "surveys"],
  } satisfies ApiQueryParams<"getCommunityFeedV2">;
  const {
    data: messageData,
    hasNextPage: hasMoreMessages,
    fetchNextPage: fetchMoreMessages,
    isPending: isLoadingMessages,
    isFetchingNextPage: isLoadingMoreMessages,
  } = useInfiniteQuery(
    communityFeedQueries.messagesInfinite({
      ...messageFeedQuery,
      Limit: COMMUNITY_FEED_ITEM_AMOUNT,
    }),
  );
  const messages = useMemo(() => messageData?.pages.flatMap((x) => x.items) ?? [], [messageData]);
  const ref = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isLoadingMoreMessages && hasMoreMessages) {
        void fetchMoreMessages();
      }
    }, [fetchMoreMessages, hasMoreMessages, isLoadingMoreMessages]),
  });
  const residentCreatePostMenuActions: ContextMenuItem[] = groupDetails
    ? [
        {
          labels: {
            default: t("page.group-details.resident-create-message-options.inform"),
          },
          value: "inform",
          icon: <Edit05Icon />,
          callbackFn: () => setResidentPostType("inform"),
          isHidden: groupDetails.type === "helpCategory",
        },
        {
          labels: {
            default: t("page.group-details.resident-create-message-options.help"),
          },
          value: "ask-help",
          icon: <MessageQuestionSquareIcon />,
          callbackFn: () => setResidentPostType("askHelp"),
          isHidden: groupDetails.id === sessionUser.marketPlaceGroupId,
        },
        {
          labels: {
            default: t("page.group-details.resident-create-message-options.marketplace"),
          },
          value: "sell-item",
          icon: <Building02Icon />,
          callbackFn: () => setResidentPostType("sellItem"),
          isHidden: groupDetails.id !== sessionUser.marketPlaceGroupId,
        },
      ]
    : [];

  const error = groupDetailsError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const isPageLoading = groupDetailsIsFetching;
  if (isPageLoading || !groupDetails) {
    return <FullSizeLoader withPadding />;
  }

  const image = groupDetails.image ? groupDetails.image : sessionUser.project.backgroundImage;
  const listRoute = groupDetails.type === "helpCategory" ? routes.helpCategories.list : routes.interestGroups.list;
  let editRoute:
    | typeof routes.interestGroups.edit
    | typeof routes.helpCategories.edit
    | typeof routes.interestGroups.editResidentGroup;
  if (groupDetails.type === "helpCategory") {
    editRoute = routes.helpCategories.edit;
  } else if (groupDetails.isResidentGroup) {
    editRoute = routes.interestGroups.editResidentGroup;
  } else {
    editRoute = routes.interestGroups.edit;
  }
  const duplicateRoute =
    groupDetails.type === "helpCategory" ? routes.helpCategories.create : routes.interestGroups.create;

  let tag: string | undefined;
  if (sessionUser.isAdmin) {
    if (groupDetails.type === "realEstate") {
      tag = t("page.discovery.group-type.real-estate");
    } else if (groupDetails.projectConnectionGroupId) {
      tag = t("page.discovery.group-type.project-connection");
    } else if (groupDetails.systemGroupId) {
      tag = t("page.discovery.group-type.platform");
    }
  }
  const menuActions: ContextMenuItem[] = [
    {
      labels: {
        default: t("common.action.edit"),
      },
      value: "edit",
      "data-testid": "context-menu-edit-btn",
      icon: <Edit05Icon />,
      type: "link",
      href: editRoute({
        slug,
        id: groupDetails.id,
      }),
      isHidden: !groupDetails.canEdit,
    },
    {
      labels: {
        default: t("common.action.duplicate"),
      },
      value: "duplicate",
      "data-testid": "context-menu-duplicate-btn",
      icon: <Copy01Icon />,
      type: "link",
      href: duplicateRoute({
        slug,
      }),
      isHidden:
        !hasPermission(canClone) ||
        !!groupDetails.systemGroupId ||
        !!groupDetails.projectConnectionGroupId ||
        !!groupDetails.isResidentGroup,
    },
    {
      labels: {
        default: groupDetails.disabled ? t("common.action.enable") : t("common.action.disable"),
      },
      value: "disable",
      icon: <Power01Icon />,
      type: "button",
      callbackFn: () => {
        if (groupDetails.disabled) {
          enableGroupMutation.mutate(
            { id: groupDetails.id, groupType: groupDetails.type },
            {
              onSuccess: () => {
                void navigate(
                  groupDetails?.type === "helpCategory"
                    ? routes.helpCategories.list({
                        slug,
                      })
                    : routes.interestGroups.list({
                        slug,
                      }),
                );
              },
            },
          );
        } else {
          setDisableGroupId(groupDetails.id);
        }
      },
      isHidden:
        !groupDetails.canDelete ||
        (!groupDetails.systemGroupId && !groupDetails.projectConnectionGroupId) ||
        groupDetails.disabled,
    },
    {
      labels: {
        default: t("common.action.delete"),
      },
      value: "delete",
      "data-testid": "context-menu-delete-btn",
      icon: <Trash02Icon />,
      type: "button",
      callbackFn: () => openDeleteModal(groupDetails.id),
      isHidden: !groupDetails.canDelete || !!groupDetails.systemGroupId || !!groupDetails.projectConnectionGroupId,
    },
  ];

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={groupDetails.name.trim() || t("page.group-details.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name:
                groupDetails.type === "helpCategory"
                  ? t("page.help-category.list.title")
                  : t("page.interest-group.list.title"),
              to: listRoute({
                slug,
              }),
            },
            {
              name: groupDetails.name,
            },
          ]}
        />
      }
      actions={
        <GroupPostActions
          canCreatePost={groupDetails.canCreatePost}
          canCreateAnnouncement={groupDetails.canCreateAnnouncement}
          isAdmin={sessionUser.isAdmin}
          slug={slug}
          groupId={groupDetails.id}
          residentCreatePostMenuActions={residentCreatePostMenuActions}
        />
      }
    >
      <PageGrid.Row>
        <PageGrid.Column size="50%">
          <Stack gap="lg">
            <EntityDetailsCard
              title={groupDetails.name}
              description={groupDetails.description}
              images={image ? [image] : []}
              actions={menuActions}
              renderCtaButton={() =>
                groupDetails.isOwner && groupDetails.isResidentGroup ? (
                  <Button data-testid="group-admin-btn" icon={<Award01Icon />} disabled={true}>
                    {t("model.interest-group.owner")}
                  </Button>
                ) : (
                  <ToggleButton
                    config={{
                      checked: {
                        label:
                          groupDetails.type === "helpCategory"
                            ? t("model.help-category.action.unfollow")
                            : t("model.interest-group.action.unfollow"),
                        icon: <CheckIcon />,
                      },
                      unchecked: {
                        label:
                          groupDetails.type === "helpCategory"
                            ? t("model.help-category.action.follow")
                            : t("model.interest-group.action.follow"),
                      },
                    }}
                    fullWidth="untilSm"
                    data-testid="group-action-follow-unfollow"
                    onClick={() => {
                      if (groupDetails.isMember) {
                        unfollowMutation.mutate({ id: groupDetails.id, groupType: groupDetails.type });
                      } else {
                        followMutation.mutate({ id: groupDetails.id, groupType: groupDetails.type });
                      }
                    }}
                    isChecked={groupDetails.isMember}
                  />
                )
              }
              renderBadges={() => (
                <>
                  {groupDetails.disabled && <Badge variant="error">{t("common.label.disabled")}</Badge>}
                  {tag && <Badge>{tag}</Badge>}
                </>
              )}
              renderBody={() => (
                <Stack gap="md" align="start">
                  {groupDetails.audience &&
                    groupDetails.audience.length > 0 &&
                    sessionUser.isAdmin &&
                    hasPermission(canCreateInterestGroup) && (
                      <aside className="w-fit">
                        <AudiencePreview audience={groupDetails.audience} readOnly />
                      </aside>
                    )}
                  {!groupDetails.disabled && (
                    <UserAvatarList
                      images={orderBy(groupDetails.members.items, (x) => !!x.avatar).map((x) => x.avatar)}
                      limit={3}
                      totalElements={groupDetails.members.total}
                      onClick={groupMembersModalOpenHandlers.toggle}
                    />
                  )}
                </Stack>
              )}
              renderFooter={() => (
                <>
                  {isContentAutoTranslated(groupDetails.originalLanguageId, sessionUser) && <AutoTranslatedLabel />}
                  {groupDetails.lastActivityAt && <LastActivityLabel lastActivityAt={groupDetails.lastActivityAt} />}
                </>
              )}
              entityId={groupDetails.id}
              entityType="group"
              otherIds={{
                groupTypeId: groupDetails.type === "interest" ? "1" : "2",
              }}
              allowShare
            />
            {groupDetails.isOwner && groupDetails.canPromote && groupDetails.hasPromotionBanner ? (
              <Card as="section">
                <Stack gap="md" align="center">
                  <MessageChatSquareIcon />
                  <span className="text-heading-3">{t("page.group-details.promote.label")}</span>
                  <Button data-testid="promote-group-button" onClick={() => setResidentPostType("promote")}>
                    {t("page.group-details.promote.button")}
                  </Button>
                  <Button variant="subtle" underline onClick={() => skipPromoteMutation.mutateAsync(undefined)}>
                    {t("common.action.skip")}
                  </Button>
                </Stack>
              </Card>
            ) : null}
            <GroupMessageList
              isLoading={isLoadingMessages}
              messages={messages}
              hasMoreMessages={hasMoreMessages}
              loaderRef={ref}
            />
          </Stack>
        </PageGrid.Column>
      </PageGrid.Row>
      <DeleteModal<string>
        title={
          groupDetails.type === "helpCategory"
            ? t("model.help-category.action.delete.confirmation")
            : t("model.interest-group.action.delete.confirmation")
        }
        description={
          groupDetails.type === "helpCategory"
            ? t("model.help-category.action.delete.description")
            : t("model.interest-group.action.delete.description")
        }
        onDelete={(id) =>
          deleteGroupMutation.mutateAsync(
            { id, groupType: groupDetails.type },
            {
              onSuccess: () =>
                void navigate(
                  groupDetails?.type === "helpCategory"
                    ? routes.helpCategories.list({ slug })
                    : routes.interestGroups.list({ slug }),
                ),
            },
          )
        }
        deleteBtnProps={{
          "data-testid": "modal-confirm-delete",
        }}
        {...deleteModalProps}
      />
      <ConfirmModal
        title={
          groupDetails.type === "helpCategory"
            ? t("model.platform.help-category.action.disable.confirmation")
            : t("model.platform.interest-group.action.disable.confirmation")
        }
        description={
          groupDetails.type === "helpCategory"
            ? t("model.platform.help-category.action.disable.description")
            : t("model.platform.interest-group.action.disable.description")
        }
        tone="warning"
        onOpenChange={(state) => {
          if (!state) {
            setDisableGroupId(null);
          }
        }}
        onReject={() => setDisableGroupId(null)}
        onResolve={async () => {
          await disableGroupMutation.mutateAsync(
            {
              id: disableGroupId!,
              groupType: groupDetails.type,
            },
            {
              onSuccess: () => {
                void navigate(
                  groupDetails?.type === "helpCategory"
                    ? routes.helpCategories.list({
                        slug,
                      })
                    : routes.interestGroups.list({
                        slug,
                      }),
                );
              },
            },
          );
          setDisableGroupId(null);
        }}
        resolveBtnProps={{
          text: t("common.action.disable"),
          "data-testid": "group-disable-modal-disable",
        }}
        isOpened={disableGroupId != null}
        shouldCloseOnEsc
        isLoading={disableGroupMutation.isPending}
      />
      <ResidentCreatePostModal
        isOpened={!!residentPostType}
        onOpenChange={(state) => {
          if (!state) {
            setResidentPostType(null);
          }
        }}
        type={residentPostType}
        groupToBePromoted={residentPostType === "promote" ? groupDetails : undefined}
        groupId={residentPostType === "promote" ? undefined : groupDetails.id}
      />
      <GroupMembersModal
        group={groupDetails}
        isOpened={isGroupMembersModalOpened}
        onOpenChange={groupMembersModalOpenHandlers.set}
      />
    </DocumentPage>
  );
}
function GroupMessageList({
  isLoading,
  messages,
  hasMoreMessages,
  loaderRef,
}: {
  isLoading: boolean;
  messages: CommunityFeedItemV2DtoPaginationResultDto["items"];
  hasMoreMessages: boolean;
  loaderRef: (node: Element | null) => void;
}): React.ReactNode {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <CardCenteredState>
        <FullSizeLoader size="sm" />
      </CardCenteredState>
    );
  }
  if (messages.length === 0) {
    return <p>{t("page.group-details.no-messages")}</p>;
  }

  return (
    <>
      <CommunityPostViewCountTrackingProvider>
        <Stack as="ul" gap="lg">
          {messages.map((message) => (
            <Fragment key={message.type + (message.message?.id || message.poll?.id || message.survey?.id)}>
              {message.type === "message" && message.message && (
                <li data-testid="community-post">
                  <CommunityMessage message={message.message} />
                </li>
              )}
              {message.type === "poll" && message.poll && (
                <li data-testid="community-poll">
                  <CommunityPoll poll={message.poll} />
                </li>
              )}
              {message.type === "survey" && message.survey && (
                <li data-testid="community-survey">
                  <CommunitySurvey survey={message.survey} />
                </li>
              )}
            </Fragment>
          ))}
        </Stack>
      </CommunityPostViewCountTrackingProvider>
      {hasMoreMessages && <CollectionLoadingState ref={loaderRef} />}
    </>
  );
}
function GroupPostActions({
  canCreatePost,
  canCreateAnnouncement,
  isAdmin,
  slug,
  groupId,
  residentCreatePostMenuActions,
}: {
  canCreatePost: boolean;
  canCreateAnnouncement: boolean;
  isAdmin: boolean;
  slug: string;
  groupId: string;
  residentCreatePostMenuActions: ContextMenuItem[];
}): React.ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const firstResidentAction = residentCreatePostMenuActions[0];

  if (!canCreatePost && !canCreateAnnouncement) {
    return null;
  }
  if (isAdmin) {
    return (
      <>
        <div className="hidden w-fit @md:flex">
          <Button
            type="link"
            href={routes.communityFeed.adminCreatePost({
              slug,
            })}
            state={
              {
                groupId,
              } satisfies MessageRouteStates["adminCreatePost"]
            }
            variant="filled"
            data-testid="group-action-create-message"
          >
            {t("page.group-details.action.create-message")}
          </Button>
        </div>
        <div className="@md:hidden">
          <IconButton
            variant="filled"
            title={t("page.group-details.action.create-message")}
            onClick={() =>
              void navigate(
                routes.communityFeed.adminCreatePost({
                  slug,
                }),
                {
                  state: {
                    groupId,
                  },
                },
              )
            }
            icon={<PlusIcon />}
            isCircular
          />
        </div>
      </>
    );
  }
  if (firstResidentAction && residentCreatePostMenuActions.length === 1) {
    return (
      <>
        <div className="hidden w-fit @md:flex">
          <Button data-testid="create-message" onClick={firstResidentAction.callbackFn}>
            {t("page.group-details.action.create-message")}
          </Button>
        </div>
        <div className="@md:hidden">
          <IconButton
            variant="filled"
            title={t("page.group-details.action.create-message")}
            onClick={firstResidentAction.callbackFn}
            icon={<PlusIcon />}
            isCircular
          />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="hidden @md:block">
        <ContextMenu
          items={residentCreatePostMenuActions}
          triggerComponent={
            <Button data-testid="create-message">{t("page.group-details.action.create-message")}</Button>
          }
        />
      </div>
      <div className="@md:hidden">
        <ContextMenu
          items={residentCreatePostMenuActions}
          triggerComponent={
            <IconButton
              variant="filled"
              title={t("page.group-details.action.create-message")}
              icon={<PlusIcon />}
              isCircular
            />
          }
        />
      </div>
    </>
  );
}
